import { useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { collection, query, where, orderBy, onSnapshot, getDocs, doc, runTransaction } from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { logActivity } from '../utils/activity';
import AppLayout from '../components/AppLayout';
import RecordBrokerPaymentModal from '../components/RecordBrokerPaymentModal';
import RecordSalePaymentModal from '../components/RecordSalePaymentModal';
import { SalePreviewModal } from './Sales';
import { useColumnFilters, type FilterColumn } from '../hooks/useColumnFilters';
import { ColumnFilterButton, ColumnFilterChips } from '../components/ColumnFilter';
import { usePaging } from '../hooks/usePaging';
import { useFitHeight } from '../hooks/useFitHeight';
import { Paging } from '../components/ui/Paging';
import { shortInvoiceNo } from '../utils/invoiceNo';
import { IconExportSm } from '../components/HeaderControls';
import { downloadCSV } from '../utils/csv';
import BulkRecordBrokerPaymentModal from '../components/BulkRecordBrokerPaymentModal';
import { Button, Modal, ModalBanner, ConfirmModal, FormField, Dropdown } from '../components/ui';
import { fmtMoney } from '../utils/currency';
import { useSettings } from '../hooks/useSettings';
import { buildEntry, postEntry, entryId, linesBrokerPayment } from '../utils/ledger';
import type { SaleInvoice, PurchaseInvoice, BrokerPayment, PaymentStatus, BankAccount, Entity } from '../types';

// A broker deal is either a sale (broker paid by the seller) or a purchase
// (commission the buyer owes a broker). Both invoice shapes carry the same
// broker* fields; `_source` says which collection the row came from so the
// payment modals write to the right place.
//
// A LineDeal is the newer per-line Broker1/Broker2 counterpart: an invoice
// can involve several different brokers across its line items (unlike the
// one legacy header-level broker above), so each distinct broker found on
// any line gets its OWN deal row here — commission summed from just that
// broker's own lines (see computeLineBrokerCommissions in Purchase.tsx/
// Sales.tsx, duplicated as lineBrokerCommissionFor below since this page
// doesn't have the raw line items, just the already-saved invoice/sale
// docs). Deliberately normalized to carry the SAME field names
// (brokerCommissionUSD/brokerPaidUSD/brokerPaymentStatus/etc.) the header
// Deal uses, so PayBadge/commissionOutstanding/the table/the detail modal
// all work on either kind without branching everywhere — only the few
// spots that truly differ (payment write path, bulk-pay eligibility) check
// `_lineBroker`.
interface LineDeal {
  _source: 'sale' | 'purchase';
  _lineBroker: true;
  id: string;               // the invoice/sale doc id (NOT unique per row — one invoice can produce several LineDeal rows, one per broker)
  invoiceNo: string;
  date: string;
  dueDate: string;
  partyId: string;
  totalAmountUSD: number;
  brokerEntityId: string;
  brokerCommissionPercent?: number;  // omitted — a line deal can span lines with different %, so no single figure applies
  brokerCommissionUSD: number;
  brokerPaidUSD: number;
  brokerPaymentStatus: PaymentStatus;
}

type Deal =
  | (SaleInvoice     & { _source: 'sale'; _lineBroker?: false })
  | (PurchaseInvoice & { _source: 'purchase'; _lineBroker?: false })
  | LineDeal;

function isLineDeal(d: Deal): d is LineDeal {
  return d._lineBroker === true;
}
// Same composite-vs-plain id scheme as the main table's row `key` — a line
// deal isn't unique by `id` alone (one invoice can produce several, one per
// broker), so its key folds in `brokerEntityId` too.
function dealKey(d: Deal): string {
  return isLineDeal(d) ? `${d._source}_${d.id}_${d.brokerEntityId}` : `${d._source}_${d.id}`;
}
function dealDate(d: Deal): string {
  if (isLineDeal(d)) return d.date;
  return d._source === 'sale' ? (d.saleDate ?? '') : (d.purchaseDate ?? '');
}
// The underlying invoice/sale's own Due Date — not a broker-specific date,
// brokers don't have one of their own. Shown so "outstanding" (commission
// not yet paid) can be told apart from "actually due" (the invoice's own
// due date has arrived) — an outstanding commission on an invoice that
// isn't due yet isn't something to chase down today.
function dealDueDate(d: Deal): string {
  return d.dueDate ?? '';
}
// Mirrors Purchase.tsx/Sales.tsx's own dueDateDisplay exactly.
function dueDateDisplay(dateStr: string) {
  const due   = new Date(dateStr + 'T00:00:00');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff  = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  const label = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return {
    label,
    badge:     diff === 0 ? 'Today' : diff > 0 ? `${diff}d left` : `${Math.abs(diff)}d over`,
    isOverdue: diff < 0,
    isSoon:    diff >= 0 && diff <= 7,
  };
}
function dealPartyId(d: Deal): string {
  if (isLineDeal(d)) return d.partyId;
  return d._source === 'sale' ? d.toEntityId : d.fromEntityId;
}
// Mirrors computeLineBrokerCommissions in Purchase.tsx/Sales.tsx — summed
// per broker across every line that names them as Broker1 or Broker2.
function lineBrokerCommissions(
  lineItems: { amountUSD: number; grossAmountUSD?: number; broker1EntityId?: string; broker1CommissionPercent?: number; broker2EntityId?: string; broker2CommissionPercent?: number }[],
): Map<string, number> {
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const totals = new Map<string, number>();
  for (const li of lineItems) {
    if (li.broker1EntityId && li.broker1CommissionPercent) {
      const amt = round2((li.grossAmountUSD ?? li.amountUSD ?? 0) * li.broker1CommissionPercent / 100);
      totals.set(li.broker1EntityId, round2((totals.get(li.broker1EntityId) ?? 0) + amt));
    }
    if (li.broker2EntityId && li.broker2CommissionPercent) {
      const amt = round2((li.grossAmountUSD ?? li.amountUSD ?? 0) * li.broker2CommissionPercent / 100);
      totals.set(li.broker2EntityId, round2((totals.get(li.broker2EntityId) ?? 0) + amt));
    }
  }
  return totals;
}
// Which % rate(s) each broker shows up at across a set of line items —
// mirrors the `linePercents` map built inline in Purchase.tsx's/Sales.tsx's
// own preview modals. A broker earning the same % on every line it's on
// gets that one number; a broker spanning lines at different rates has no
// single figure that applies, so it's left out (the caller shows a dash).
function lineBrokerPercents(
  lineItems: { broker1EntityId?: string; broker1CommissionPercent?: number; broker2EntityId?: string; broker2CommissionPercent?: number }[],
): Map<string, number> {
  const sets = new Map<string, Set<number>>();
  for (const li of lineItems) {
    if (li.broker1EntityId && li.broker1CommissionPercent) {
      const s = sets.get(li.broker1EntityId) ?? new Set<number>();
      s.add(li.broker1CommissionPercent);
      sets.set(li.broker1EntityId, s);
    }
    if (li.broker2EntityId && li.broker2CommissionPercent) {
      const s = sets.get(li.broker2EntityId) ?? new Set<number>();
      s.add(li.broker2CommissionPercent);
      sets.set(li.broker2EntityId, s);
    }
  }
  const out = new Map<string, number>();
  for (const [brokerEntityId, pcts] of sets) {
    if (pcts.size === 1) out.set(brokerEntityId, [...pcts][0]);
  }
  return out;
}

// ── Helpers (duplicated per-page, matching this codebase's convention) ───────

// Local calendar day — never toISOString(), which is UTC: in any timezone
// ahead of UTC (India, Europe…) local midnight is still the previous day in
// UTC, so every computed date (due dates, today, filter ranges) came out a day early.
function toDateStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function todayISO() { return toDateStr(new Date()); }

// Invoice No is stored in full, but shown here as just the prefix + last 4
// digits to keep the column compact. Keeping the prefix matters — a bare
// last-4 truncation collapses different prefixes into the same number.
function lotNoDisplay(invoiceNo?: string) {
  if (!invoiceNo) return '—';
  const m = invoiceNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : invoiceNo.slice(-4);
}
function commissionOutstanding(inv: { brokerCommissionUSD?: number; brokerPaidUSD?: number }): number {
  return Math.max(0, (inv.brokerCommissionUSD ?? 0) - (inv.brokerPaidUSD ?? 0));
}

// One invoice's brokerage on the list: every broker deal on it added up.
interface InvoiceRow {
  key: string; source: 'sale' | 'purchase'; id: string; invoiceNo: string;
  date: string; dueDate: string; partyId: string; amount: number;
  deals: Deal[]; commission: number; paid: number;
}
function rowStatus(r: InvoiceRow): PaymentStatus {
  return r.paid >= r.commission - 0.005 ? 'paid' : r.paid > 0.005 ? 'partial' : 'unpaid';
}
function rowStatusLabel(r: InvoiceRow): string {
  const st = rowStatus(r);
  return st === 'paid' ? 'Paid' : st === 'partial' ? 'Partly paid' : 'Unpaid';
}
const ROW_DUE_ORDER = ['Overdue', 'Due this week', 'Later', 'Paid'];
// The list opens on brokerage still owed (Status: Unpaid + Partly paid);
// Paid is one tick away in the Status filter, and Clear comes back here.
const DEFAULT_STATUS = ['Unpaid', 'Partly paid'];
function onDefaultFilters(selected: Record<string, string[]>): boolean {
  const keys = Object.keys(selected);
  return keys.length === 1 && keys[0] === 'status'
    && selected.status.length === DEFAULT_STATUS.length && DEFAULT_STATUS.every(v => selected.status.includes(v));
}
function rowDueBucket(r: InvoiceRow): string {
  if (rowStatus(r) === 'paid') return 'Paid';
  if (!r.dueDate) return 'Later';
  const dd = dueDateDisplay(r.dueDate);
  return dd.isOverdue ? 'Overdue' : dd.isSoon ? 'Due this week' : 'Later';
}

type DateFilter = '1m' | '3m' | '6m' | '1y' | '2y' | 'custom';

function computeDateRange(
  dateFilter: DateFilter, customFrom: string, customTo: string,
): { start: string | null; end: string | null } | null {
  // The period is on the DUE DATE (asked for): every overdue invoice, however
  // old, plus everything falling due in the next N months (1M = overdue +
  // due within a month from today, and so on). Custom = due between two dates.
  const now = new Date();
  const ahead = (months: number) => ({ start: null, end: toDateStr(new Date(now.getFullYear(), now.getMonth() + months, now.getDate() + 1)) });
  switch (dateFilter) {
    case '1m': return ahead(1);
    case '3m': return ahead(3);
    case '6m': return ahead(6);
    case '1y': return ahead(12);
    case '2y': return ahead(24);
    case 'custom': {
      if (!customFrom || !customTo) return null;
      const endDate = new Date(customTo + 'T00:00:00');
      endDate.setDate(endDate.getDate() + 1);
      return { start: customFrom, end: toDateStr(endDate) };
    }
  }
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconCalendar() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M1.5 6h11M4.5 1v3M9.5 1v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconPay() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="3" width="12" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M1 6h12" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="4" cy="9" r="1" fill="currentColor" />
  </svg>;
}
function IconCash({ size = 13 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <rect x="1" y="3.5" width="12" height="7" rx="1.3" stroke="currentColor" strokeWidth="1.3" />
    <circle cx="7" cy="7" r="1.6" stroke="currentColor" strokeWidth="1.2" />
  </svg>;
}
function IconBank({ size = 13 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <rect x="1.5" y="6.5" width="11" height="6" rx="0.8" stroke="currentColor" strokeWidth="1.3" />
    <path d="M1.5 6.5L7 2l5.5 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <path d="M3.7 9v2.2M7 9v2.2M10.3 9v2.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>;
}
function IconEmptyDiamond() {
  return <svg width="36" height="36" viewBox="0 0 16 16" fill="none">
    <polygon points="4,2 12,2 14.5,6 8,14.5 1.5,6" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M8 14.5V6M1.5 6h13M4 2l4 4M12 2l-4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}

// ── PayBadge — commission payment status ──────────────────────────────────────

function PayBadge({ inv }: { inv: { brokerPaymentStatus?: PaymentStatus; brokerPaidUSD?: number; brokerCommissionUSD?: number } }) {
  const status = inv.brokerPaymentStatus ?? 'unpaid';
  const paid   = inv.brokerPaidUSD ?? 0;
  const total  = inv.brokerCommissionUSD ?? 0;
  const pct    = total > 0 ? Math.round((paid / total) * 100) : 0;
  if (status === 'paid') return (
    <span className="badge" style={{ background: 'var(--success-bg)', color: 'var(--success)', border: '1px solid var(--success)' }}>Paid</span>
  );
  if (status === 'partial') return (
    <span className="badge" style={{ background: 'var(--warning-bg)', color: '#c2410c', border: '1px solid var(--warning)' }}>{pct}% paid</span>
  );
  return (
    <span className="badge" style={{ background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid var(--danger)' }}>Unpaid</span>
  );
}

// ── Detail modal header facts — matches AP / AR / Sales ────────────────────────

function VDivider() {
  return <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)' }} />;
}
function FactPanel({ children }: { children: ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14,
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 999, padding: '7px 18px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      {children}
    </div>
  );
}
function MiniFact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)' }}>
        {value}
      </div>
    </div>
  );
}
function LotPill({ children }: { children: ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
      fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 5, letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {children}
    </div>
  );
}

// ── Commission detail modal ───────────────────────────────────────────────────

function CommissionDetailModal({
  invoice, onClose, onPay, entities, allDeals,
}: {
  invoice: Deal | null;
  onClose: () => void;
  onPay:   (inv: Deal) => void;
  entities: Entity[];
  // Every deal currently loaded on the page (already scoped to its broker
  // filter + date-period, same list the main table renders) — used to build
  // the "All Commission" statement for this invoice's own broker, so a user
  // can see everything owed to that broker, not just this one invoice.
  allDeals: Deal[];
}) {
  const [showAll, setShowAll] = useState(false);
  // Every other deal (sale or purchase, header or per-line) belonging to
  // this SAME broker, from whatever's already loaded on the page (so it's
  // scoped to the page's own broker/date filters, not a fresh full-history
  // fetch) — lets "View All" answer "how much do I owe this broker overall",
  // not just this one invoice.
  const brokerDeals = useMemo(
    () => (invoice ? allDeals.filter(d => d.brokerEntityId === invoice.brokerEntityId) : []),
    [allDeals, invoice],
  );
  const brokerTotals = useMemo(() => {
    const commission = brokerDeals.reduce((s, d) => s + (d.brokerCommissionUSD ?? 0), 0);
    const paidSum     = brokerDeals.reduce((s, d) => s + (d.brokerPaidUSD ?? 0), 0);
    return { commission, paid: paidSum, outstanding: Math.max(0, commission - paidSum) };
  }, [brokerDeals]);
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const coll  = invoice?._source === 'purchase' ? 'invoices' : 'sales';

  const [payments,    setPayments]    = useState<BrokerPayment[]>([]);
  const [paysLoading, setPaysLoading] = useState(false);

  const lineDeal = invoice ? isLineDeal(invoice) : false;
  // The shared brokerPayments subcollection holds payments for BOTH the
  // invoice's header-level broker (brokerEntityId left unset on those docs)
  // and any per-line brokers (brokerEntityId set to the specific one) — so
  // this deal's own history must filter to just its own kind, or a
  // header-broker deal would show per-line payments mixed in and vice
  // versa.
  useEffect(() => {
    if (!invoice) { setPayments([]); setPaysLoading(false); return; }
    const db = getClientDb();
    if (!db) { setPaysLoading(false); return; }
    setPaysLoading(true);
    const unsub = onSnapshot(
      collection(db, coll, invoice.id, 'brokerPayments'),
      snap => {
        const pays = snap.docs
          .map(d => ({ id: d.id, ...d.data() } as BrokerPayment))
          .filter(p => lineDeal ? p.brokerEntityId === invoice.brokerEntityId : !p.brokerEntityId)
          .sort((a, b) => (a.paidDate ?? '').localeCompare(b.paidDate ?? ''));
        setPayments(pays);
        setPaysLoading(false);
      },
      err => { console.error('Broker payment history error:', err); setPaysLoading(false); },
    );
    return unsub;
  }, [invoice?.id, lineDeal, invoice?.brokerEntityId]);

  // ── Edit state ────────────────────────────────────────────────────────────
  const [editingPay, setEditingPay] = useState<BrokerPayment | null>(null);
  const [editForm,   setEditForm]   = useState({ amount: '', paidDate: '', referenceNo: '', notes: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError,  setEditError]  = useState('');

  function openEdit(pay: BrokerPayment) {
    setEditingPay(pay);
    setEditForm({
      amount:      String(pay.amount ?? 0),
      paidDate:    pay.paidDate ?? todayISO(),
      referenceNo: pay.referenceNo ?? '',
      notes:       pay.notes      ?? '',
    });
    setEditError('');
  }

  async function handleEditSave() {
    if (!editingPay || !invoice || !business) return;
    const newAmt = parseFloat(editForm.amount);
    if ((editingPay.gstTotalUSD ?? 0) > 0) { setEditError('This payout has GST on it. Delete it and record it again to change it.'); return; }
    if (isNaN(newAmt) || newAmt <= 0) { setEditError('Enter a valid amount.'); return; }
    if (!editForm.paidDate) { setEditError('Select a date.'); return; }

    setEditSaving(true); setEditError('');
    const db = getClientDb();
    if (!db) { setEditError('Database not connected.'); setEditSaving(false); return; }

    const oldAmt  = editingPay.amount ?? 0;
    const diff    = newAmt - oldAmt;
    const newPaid = Math.max(0, (invoice.brokerPaidUSD ?? 0) + diff);
    const newStatus: PaymentStatus =
      newPaid >= (invoice.brokerCommissionUSD ?? 0) - 0.005 ? 'paid'
      : newPaid > 0 ? 'partial' : 'unpaid';
    // If this payment was converted (bankCurrency set), re-derive the new
    // bank-side amount proportionally via the stored exchangeRate instead of
    // applying the raw base-currency diff — mirrors AP.tsx/AR.tsx's own
    // edit-payment fix. Unconverted payments keep today's exact behavior:
    // bankDiff === diff.
    const newBankAmt = editingPay.exchangeRate ? newAmt * editingPay.exchangeRate : newAmt;
    const oldBankAmt = editingPay.bankAmount ?? oldAmt;
    const bankDiff   = newBankAmt - oldBankAmt;

    try {
      await runTransaction(db, async tx => {
        const payRef = doc(db, coll, invoice.id, 'brokerPayments', editingPay.id);
        const invRef = doc(db, coll, invoice.id);
        const accRef = (editingPay.bankAccountId && bankDiff !== 0) ? doc(db, 'bankAccounts', editingPay.bankAccountId) : null;

        // All reads before any writes — Firestore transactions require it.
        const accSnap = accRef ? await tx.get(accRef) : null;

        tx.update(payRef, {
          amount:      newAmt,
          bankAmount:  editingPay.bankCurrency ? newBankAmt : null,
          paidDate:    editForm.paidDate,
          referenceNo: editForm.referenceNo.trim() || null,
          notes:       editForm.notes.trim()        || null,
        });
        logActivity(tx, db, business.id, user, {
          module: 'broker', action: 'edit', recordId: invoice.id, recordNo: invoice.invoiceNo, partyId: invoice.brokerEntityId,
          amountUSD: newAmt, prevAmountUSD: oldAmt, note: editingPay.bankAccountName,
        });

        // A line deal has no single header-level broker field on the invoice
        // to update — its paid/outstanding is always computed live from the
        // brokerPayments subcollection (see Brokers.tsx's lineDeals), so
        // writing here would incorrectly stamp this broker's own number onto
        // a field that may belong to a different (header) broker entirely.
        if (!lineDeal) {
          tx.update(invRef, { brokerPaidUSD: newPaid, brokerPaymentStatus: newStatus, updatedAt: new Date().toISOString() });
        }

        if (accRef && accSnap?.exists()) {
          const cur = (accSnap.data() as BankAccount).currentBalance;
          tx.update(accRef, { currentBalance: cur - bankDiff });
        }

        const btId = editingPay.bankTransactionId;
        if (btId && bankDiff !== 0) {
          tx.update(doc(db, 'bankTransactions', btId), { amount: newBankAmt });
        }

        // Cascade to the ledger — overwrite this payout's entry in place.
        if (editingPay.bankAccountId && invoice.brokerEntityId) {
          postEntry(tx, db, buildEntry({
            businessId: business.id, sourceType: 'broker_payment', sourceId: editingPay.id,
            sourceCollection: `${coll}/${invoice.id}/brokerPayments`,
            date: editForm.paidDate, currency: editingPay.currency ?? 'USD',
            createdBy: user?.id ?? editingPay.createdBy ?? 'system',
            note: `Broker payout — ${invoice.invoiceNo}`,
            lines: linesBrokerPayment(newAmt, invoice.brokerEntityId, editingPay.bankAccountId),
          }), entryId('broker_payment', editingPay.id));
        }
      });

      setEditingPay(null);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Failed to save.');
    }
    setEditSaving(false);
  }

  // ── Delete state ──────────────────────────────────────────────────────────
  const [deletingPay,  setDeletingPay]  = useState<BrokerPayment | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError,  setDeleteError]  = useState('');

  async function handleDeletePayment() {
    if (!deletingPay || !invoice || !business) return;
    setDeleteSaving(true); setDeleteError('');
    const db = getClientDb();
    if (!db) { setDeleteError('Database not connected.'); setDeleteSaving(false); return; }

    const amt     = deletingPay.amount ?? 0;
    // Refund the bank-side (converted) amount if this payment was converted
    // — falls back to `amt` for every pre-existing same-currency payment,
    // unchanged behavior. Mirrors AP.tsx/AR.tsx's own delete-payment fix.
    const bankAmt = deletingPay.bankAmount ?? amt;
    const newPaid = Math.max(0, (invoice.brokerPaidUSD ?? 0) - amt);
    const newStatus: PaymentStatus =
      newPaid >= (invoice.brokerCommissionUSD ?? 0) - 0.005 ? 'paid'
      : newPaid > 0 ? 'partial' : 'unpaid';

    try {
      await runTransaction(db, async tx => {
        const payRef = doc(db, coll, invoice.id, 'brokerPayments', deletingPay.id);
        const invRef = doc(db, coll, invoice.id);
        const accRef = deletingPay.bankAccountId ? doc(db, 'bankAccounts', deletingPay.bankAccountId) : null;

        // All reads before any writes — Firestore transactions require it.
        const accSnap = accRef ? await tx.get(accRef) : null;

        tx.delete(payRef);
        logActivity(tx, db, business.id, user, {
          module: 'broker', action: 'delete', recordId: invoice.id, recordNo: invoice.invoiceNo, partyId: invoice.brokerEntityId,
          amountUSD: amt, note: deletingPay.bankAccountName,
        });
        // Same reasoning as handleEditSave above — skip the invoice-level
        // field for a line deal, it isn't the source of truth for it.
        if (!lineDeal) {
          tx.update(invRef, { brokerPaidUSD: newPaid, brokerPaymentStatus: newStatus, updatedAt: new Date().toISOString() });
        }

        if (accRef && accSnap?.exists()) {
          const cur = (accSnap.data() as BankAccount).currentBalance;
          tx.update(accRef, { currentBalance: cur + bankAmt });
        }

        const btId = deletingPay.bankTransactionId;
        if (btId) tx.delete(doc(db, 'bankTransactions', btId));

        // Remove this payout's ledger entry.
        tx.delete(doc(db, 'journalEntries', entryId('broker_payment', deletingPay.id)));
      });

      setDeletingPay(null);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete.');
    }
    setDeleteSaving(false);
  }

  if (!invoice) return null;

  const isPurchase   = invoice._source === 'purchase';
  const partyLabel   = isPurchase ? 'Supplier' : 'Customer';
  const customerName = entities.find(e => e.id === dealPartyId(invoice))?.companyName ?? '—';
  const dateStr      = dealDate(invoice);
  const brokerName   = invoice.brokerEntityId ? (entities.find(e => e.id === invoice.brokerEntityId)?.companyName ?? '—') : '—';
  const commission   = invoice.brokerCommissionUSD ?? 0;
  const paid         = invoice.brokerPaidUSD ?? 0;
  const owed         = commissionOutstanding(invoice);
  const totalPaidFromRecords = payments.reduce((s, p) => s + (p.amount ?? 0), 0);

  return (
    <>
    <Modal
      open
      onClose={onClose}
      hideHead
      size="xl"
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
          {(invoice.brokerPaymentStatus ?? 'unpaid') !== 'paid' && (
            <Button variant="primary" icon={<IconPay />} onClick={() => { onClose(); onPay(invoice); }}>
              Record Payment
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      }
    >
      <ModalBanner
        title={brokerName}
        eyebrow={`Broker Commission · ${isPurchase ? 'Purchase' : 'Sale'}${lineDeal ? ' · Per-Line' : ''}`}
        right={
          <FactPanel>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
                Invoice No
              </div>
              <LotPill>{lotNoDisplay(invoice.invoiceNo)}</LotPill>
            </div>
            <VDivider />
            <MiniFact
              label="Date"
              value={dateStr
                ? new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '—'}
            />
            <VDivider />
            <PayBadge inv={invoice} />
          </FactPanel>
        }
        onClose={onClose}
      />

      {/* ── Commission summary (this one invoice) ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <SectionLabel>Commission Summary · This Invoice</SectionLabel>
        {brokerDeals.length > 1 && (
          <button
            type="button"
            onClick={() => setShowAll(s => !s)}
            style={{
              fontSize: 11.5, fontWeight: 700, color: 'var(--primary)', background: 'none',
              border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            {showAll ? 'Hide' : 'View'} All Commission — {brokerName} ({brokerDeals.length})
          </button>
        )}
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 1,
        border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
        overflow: 'hidden', marginBottom: 20,
      }}>
        {[
          // Party moved here from the header fact panel — once "All
          // Commission" is open it already has its own Party column per
          // row, so showing it a second time up top was redundant.
          { label: partyLabel, val: null as number | null, text: customerName, color: 'var(--text-1)' },
          { label: `${partyLabel === 'Supplier' ? 'Purchase' : 'Sale'} Amount`, val: invoice.totalAmountUSD, color: 'var(--text-1)' },
          // Per-line deals can span lines with different rates — no single %
          // applies, so this shows a dash rather than a misleading "0%".
          { label: 'Commission %',   val: null as number | null, pct: invoice.brokerCommissionPercent, color: 'var(--text-1)' },
          { label: 'Commission Owed', val: commission, color: 'var(--text-1)' },
          { label: 'Paid',           val: paid, color: 'var(--success)' },
          { label: 'Outstanding',    val: owed, color: owed > 0 ? 'var(--danger)' : 'var(--text-3)' },
        ].map(({ label, val, pct, text, color }) => (
          <div key={label} style={{ background: 'var(--surface-2)', padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, marginBottom: 4 }}>{label}</div>
            <div style={{
              fontWeight: 700, fontSize: 14, color,
              fontFamily: text ? 'inherit' : 'var(--font-num)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {val !== null ? money(val) : pct != null ? `${pct}%` : text ?? '—'}
            </div>
          </div>
        ))}
      </div>

      {/* ── All Commission for this broker — every deal currently loaded on
           the page (so bounded by its broker/date filters), so the user can
           see everything owed to this one broker, not just this invoice.
           Collapsed by default — an additive option, not a replacement for
           the single-invoice summary above. ── */}
      {showAll && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <SectionLabel>All Commission — {brokerName}</SectionLabel>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {brokerDeals.length} deal{brokerDeals.length !== 1 ? 's' : ''} ·{' '}
              <span style={{ fontWeight: 700, color: brokerTotals.outstanding > 0 ? 'var(--danger)' : 'var(--text-2)' }}>
                {money(brokerTotals.outstanding)} outstanding
              </span>
            </div>
          </div>
          <div style={{ overflowX: 'auto', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  {['Date', 'Party', 'Type', 'Amount', 'Commission %', 'Commission Owed', 'Paid', 'Outstanding'].map((h, i) => (
                    <th key={i} style={{
                      padding: '7px 12px', textAlign: i >= 3 ? 'right' : 'left', fontWeight: 600,
                      color: 'var(--text-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em',
                      borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {brokerDeals
                  .slice()
                  .sort((a, b) => dealDate(b).localeCompare(dealDate(a)))
                  .map((d, idx) => {
                    const dOwed = commissionOutstanding(d);
                    const isCurrent = dealKey(d) === dealKey(invoice);
                    const dDate = dealDate(d);
                    return (
                      <tr
                        key={dealKey(d)}
                        style={{ background: isCurrent ? 'var(--primary-light)' : idx % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)' }}
                      >
                        <td style={{ padding: '8px 12px', fontFamily: 'var(--font-num)', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                          {dDate ? new Date(dDate + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-1)' }}>
                          {entities.find(e => e.id === dealPartyId(d))?.companyName ?? '—'}
                          {isCurrent && (
                            <span style={{
                              marginLeft: 6, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em',
                              color: 'var(--primary)', padding: '1px 5px', borderRadius: 4, background: 'var(--surface)',
                              border: '1px solid var(--primary)',
                            }}>This</span>
                          )}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{
                            fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                            padding: '1px 5px', borderRadius: 4,
                            background: d._source === 'purchase' ? 'var(--warning-bg)' : 'var(--primary-light)',
                            color: d._source === 'purchase' ? '#c2410c' : 'var(--primary)',
                          }}>{d._source === 'purchase' ? 'Buy' : 'Sale'}</span>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--text-2)' }}>{money(d.totalAmountUSD ?? 0)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--text-2)' }}>
                          {d.brokerCommissionPercent != null ? `${d.brokerCommissionPercent}%` : <span style={{ opacity: 0.4 }}>—</span>}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--text-1)' }}>{money(d.brokerCommissionUSD ?? 0)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--success)' }}>
                          {(d.brokerPaidUSD ?? 0) > 0 ? money(d.brokerPaidUSD ?? 0) : <span style={{ opacity: 0.4 }}>—</span>}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, color: dOwed > 0 ? 'var(--danger)' : 'var(--text-3)' }}>{money(dOwed)}</td>
                      </tr>
                    );
                  })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--surface-2)', borderTop: '2px solid var(--border)' }}>
                  <td colSpan={5} style={{ padding: '7px 12px', fontWeight: 600, fontSize: 12 }}>Total</td>
                  <td style={{ padding: '7px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, fontSize: 12 }}>{money(brokerTotals.commission)}</td>
                  <td style={{ padding: '7px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, fontSize: 12, color: 'var(--success)' }}>{money(brokerTotals.paid)}</td>
                  <td style={{ padding: '7px 12px', textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, fontSize: 12, color: brokerTotals.outstanding > 0 ? 'var(--danger)' : 'var(--text-3)' }}>{money(brokerTotals.outstanding)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ── Payment History ── */}
      <div style={{ marginTop: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <SectionLabel>Payment History</SectionLabel>
          {payments.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {payments.length} payment{payments.length !== 1 ? 's' : ''} ·{' '}
              <span style={{ fontWeight: 700, color: 'var(--success)' }}>{money(totalPaidFromRecords)} total</span>
            </div>
          )}
        </div>

        {paysLoading ? (
          <div className="skeleton" style={{ height: 60, borderRadius: 8 }} />
        ) : payments.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-3)', padding: '10px 0' }}>No payments recorded yet.</div>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  {['Date', 'Amount', 'Paid From', 'Reference', 'Notes', ''].map((h, i) => (
                    <th key={i} style={{ padding: '7px 12px', textAlign: i === 5 ? 'right' : 'left', fontWeight: 600, color: 'var(--text-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payments.map((pay, idx) => {
                  const amt     = pay.amount ?? 0;
                  const accName = pay.bankAccountName ?? '—';
                  const cur     = pay.currency ?? 'USD';
                  const dateStr = pay.paidDate
                    ? new Date(pay.paidDate + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
                    : '—';
                  return (
                    <tr key={pay.id ?? idx} style={{ background: idx % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)' }}>
                      <td style={{ padding: '8px 12px', fontFamily: 'var(--font-num)', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{dateStr}</td>
                      <td style={{ padding: '8px 12px', fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--danger)', whiteSpace: 'nowrap' }}>{fmtMoney(amt, cur)}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-3)' }}>
                          {accName.toLowerCase().includes('cash') ? <IconCash /> : <IconBank />}
                          <span style={{ fontWeight: 500, color: 'var(--text-1)' }}>{accName}</span>
                          {pay.currency && <span style={{ fontSize: 10, color: 'var(--text-3)', padding: '1px 4px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 3 }}>{pay.currency}</span>}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', fontFamily: 'var(--font-num)', color: pay.referenceNo ? 'var(--text-1)' : 'var(--text-3)' }}>{pay.referenceNo || '—'}</td>
                      <td style={{ padding: '8px 12px', color: 'var(--text-3)' }}>{pay.notes || '—'}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div className="action-row" style={{ justifyContent: 'flex-end' }}>
                          <button
                            title="Edit"
                            onClick={() => openEdit(pay)}
                            style={{ padding: '4px 8px', border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface)', color: 'var(--text-2)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                          >Edit</button>
                          <button
                            title="Delete"
                            onClick={() => { setDeletingPay(pay); setDeleteError(''); }}
                            style={{ padding: '4px 8px', border: '1.5px solid color-mix(in srgb, var(--danger) 35%, transparent)', borderRadius: 'var(--r-sm)', background: 'var(--danger-bg)', color: 'var(--danger)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                          >Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--surface-2)', borderTop: '2px solid var(--border)' }}>
                  <td style={{ padding: '7px 12px', fontWeight: 600, fontSize: 11, color: 'var(--text-3)' }}>Total</td>
                  <td style={{ padding: '7px 12px', fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--danger)' }}>{money(totalPaidFromRecords)}</td>
                  <td colSpan={4} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </Modal>

    {/* ── Edit payment modal ── */}
    <Modal
      open={!!editingPay}
      onClose={() => setEditingPay(null)}
      title="Edit Payment"
      subtitle={editingPay ? `${editingPay.bankAccountName ?? '—'} · ${editingPay.paidDate ?? ''}` : ''}
      size="sm"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {editError && <div className="msg msg-error" style={{ margin: 0 }}>{editError}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setEditingPay(null)} disabled={editSaving}>Cancel</Button>
            <Button variant="primary" onClick={handleEditSave} loading={editSaving}>Save Changes</Button>
          </div>
        </div>
      }
    >
      <div className="form-row-2">
        <FormField label={`Amount (${editingPay?.currency ?? 'USD'})`} required>
          <input
            type="number" step="0.01" min="0.01"
            value={editForm.amount}
            onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))}
            autoFocus
          />
        </FormField>
        <FormField label="Payment Date" required>
          <input
            type="date"
            value={editForm.paidDate}
            onChange={e => setEditForm(f => ({ ...f, paidDate: e.target.value }))}
          />
        </FormField>
      </div>
      <FormField label="Reference No.">
        <input
          value={editForm.referenceNo}
          onChange={e => setEditForm(f => ({ ...f, referenceNo: e.target.value }))}
          placeholder="Bank ref, cheque no., etc."
          style={{ fontFamily: 'var(--font-num)' }}
        />
      </FormField>
      <FormField label="Notes (optional)">
        <textarea
          value={editForm.notes}
          onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
          style={{ minHeight: 52 }}
        />
      </FormField>
    </Modal>

    {/* ── Delete confirm ── */}
    <ConfirmModal
      open={!!deletingPay}
      onClose={() => setDeletingPay(null)}
      onConfirm={handleDeletePayment}
      title="Delete Payment"
      message={
        deletingPay
          ? <>Delete payment of <strong>{money(deletingPay.amount ?? 0)}</strong> on {deletingPay.paidDate}?
              This will reverse the commission balance and refund the bank account.</>
          : null
      }
      confirmLabel="Delete Payment"
      loading={deleteSaving}
      error={deleteError}
    />
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function BrokersPage() {
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);
  const [sales,       setSales]       = useState<SaleInvoice[]>([]);
  const [purchases,   setPurchases]   = useState<PurchaseInvoice[]>([]);
  const [entities,    setEntities]    = useState<Entity[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [dateFilter,  setDateFilter]  = useState<DateFilter>('3m');
  const [customFrom,  setCustomFrom]  = useState('');
  const [customTo,    setCustomTo]    = useState('');
  const [payTarget,   setPayTarget]   = useState<Deal | null>(null);
  const [detailInv,   setDetailInv]   = useState<Deal | null>(null);
  const [salePayTarget, setSalePayTarget] = useState<SaleInvoice | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkOpen,     setBulkOpen]    = useState(false);

  const dateRange = useMemo(
    () => computeDateRange(dateFilter, customFrom, customTo),
    [dateFilter, customFrom, customTo],
  );

  useEffect(() => {
    if (!dateRange) { setSales([]); setPurchases([]); setLoading(false); return; }
    const db = getClientDb();
    if (!db) return;
    setLoading(true);

    const u1 = onSnapshot(
      query(
        collection(db, 'sales'),
        ...(dateRange.start ? [where('dueDate', '>=', dateRange.start)] : []),
        ...(dateRange.end ? [where('dueDate', '<', dateRange.end)] : []),
        orderBy('dueDate', 'desc'),
      ),
      snap => { setSales(snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice))); setLoading(false); },
      err => { console.error('[Brokers] sales query failed:', err); setLoading(false); },
    );
    // Purchases where the buyer owes a broker a commission — same date-window
    // shape, keyed on purchaseDate.
    const u3 = onSnapshot(
      query(
        collection(db, 'invoices'),
        ...(dateRange.start ? [where('dueDate', '>=', dateRange.start)] : []),
        ...(dateRange.end ? [where('dueDate', '<', dateRange.end)] : []),
        orderBy('dueDate', 'desc'),
      ),
      snap => { setPurchases(snap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseInvoice))); setLoading(false); },
      err => { console.error('[Brokers] purchases query failed:', err); setLoading(false); },
    );
    const u2 = onSnapshot(
      query(collection(db, 'entities'), orderBy('companyName')),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))),
    );
    return () => { u1(); u2(); u3(); };
  }, [dateRange?.start, dateRange?.end]);

  const brokers = useMemo(() => entities.filter(e => e.types.includes('broker')), [entities]);
  function entityName(id?: string) { return id ? (entities.find(e => e.id === id)?.companyName ?? '—') : '—'; }

  // Per-line Broker1/Broker2 deals — commission summed live from each
  // invoice/sale's own line items (there's no cached field for this, since
  // one invoice can involve several different line-level brokers). One row
  // per distinct broker per invoice.
  const lineDealsRaw = useMemo(() => {
    const rows: Omit<LineDeal, 'brokerPaidUSD' | 'brokerPaymentStatus'>[] = [];
    for (const inv of sales) {
      const percents = lineBrokerPercents(inv.lineItems ?? []);
      for (const [brokerEntityId, commissionUSD] of lineBrokerCommissions(inv.lineItems ?? [])) {
        if (commissionUSD <= 0.005) continue;
        rows.push({
          _source: 'sale', _lineBroker: true, id: inv.id, invoiceNo: inv.invoiceNo,
          date: inv.saleDate ?? '', dueDate: inv.dueDate ?? '', partyId: inv.toEntityId, totalAmountUSD: inv.totalAmountUSD ?? 0,
          brokerEntityId, brokerCommissionPercent: percents.get(brokerEntityId), brokerCommissionUSD: commissionUSD,
        });
      }
    }
    for (const inv of purchases) {
      const percents = lineBrokerPercents(inv.lineItems ?? []);
      for (const [brokerEntityId, commissionUSD] of lineBrokerCommissions(inv.lineItems ?? [])) {
        if (commissionUSD <= 0.005) continue;
        rows.push({
          _source: 'purchase', _lineBroker: true, id: inv.id, invoiceNo: inv.invoiceNo,
          date: inv.purchaseDate ?? '', dueDate: inv.dueDate ?? '', partyId: inv.fromEntityId, totalAmountUSD: inv.totalAmountUSD ?? 0,
          brokerEntityId, brokerCommissionPercent: percents.get(brokerEntityId), brokerCommissionUSD: commissionUSD,
        });
      }
    }
    return rows;
  }, [sales, purchases]);

  // Paid amounts for those rows — one read per distinct invoice that has at
  // least one line-level broker (not per broker-per-invoice, since several
  // line-brokers on the same invoice share that invoice's one
  // brokerPayments subcollection), re-fetched whenever the set of such
  // invoices changes (including right after a payment is recorded, since
  // that mutates `sales`/`purchases` via the live listener above, which
  // flows into `lineDealsRaw`).
  const [lineBrokerPaid, setLineBrokerPaid] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    const db = getClientDb();
    if (!db || lineDealsRaw.length === 0) { setLineBrokerPaid(new Map()); return; }
    let cancelled = false;
    const seen = new Set<string>();
    const invoiceRefs: { source: 'sale' | 'purchase'; id: string }[] = [];
    for (const d of lineDealsRaw) {
      const key = `${d._source}|${d.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      invoiceRefs.push({ source: d._source, id: d.id });
    }
    (async () => {
      const result = new Map<string, number>();
      await Promise.all(invoiceRefs.map(async ({ source, id }) => {
        const coll = source === 'purchase' ? 'invoices' : 'sales';
        const snap = await getDocs(collection(db, coll, id, 'brokerPayments'));
        for (const d of snap.docs) {
          const pay = d.data() as BrokerPayment;
          if (!pay.brokerEntityId) continue;
          const k = `${source}_${id}_${pay.brokerEntityId}`;
          result.set(k, Math.round(((result.get(k) ?? 0) + (pay.amount ?? 0)) * 100) / 100);
        }
      }));
      if (!cancelled) setLineBrokerPaid(result);
    })();
    return () => { cancelled = true; };
  }, [lineDealsRaw]);

  const lineDeals = useMemo<LineDeal[]>(() => lineDealsRaw.map(d => {
    const paid = lineBrokerPaid.get(`${d._source}_${d.id}_${d.brokerEntityId}`) ?? 0;
    const status: PaymentStatus = paid >= d.brokerCommissionUSD - 0.005 ? 'paid' : paid > 0 ? 'partial' : 'unpaid';
    return { ...d, brokerPaidUSD: paid, brokerPaymentStatus: status };
  }), [lineDealsRaw, lineBrokerPaid]);

  // Only invoices that actually have a broker (and, when a specific broker is
  // selected, only that broker's deals) belong on this page — sales and
  // buyer-paid purchase commissions (both the legacy header-level broker and
  // the newer per-line ones) merged into one list, newest first.
  const deals = useMemo<Deal[]>(() => {
    const rows: Deal[] = [
      ...sales.map(inv => ({ ...inv, _source: 'sale' as const })),
      ...purchases.map(inv => ({ ...inv, _source: 'purchase' as const })),
      ...lineDeals,
    ];
    return rows
      .filter(inv => {
        if (!inv.brokerEntityId) return false;
        return true;
      })
      .sort((a, b) => dealDate(b).localeCompare(dealDate(a)));
  }, [sales, purchases, lineDeals]);


  useEffect(() => { setSelectedIds(new Set()); }, [dateFilter, customFrom, customTo]);

  // ── One row per invoice ──────────────────────────────────────────────────
  // A deal is one broker on one invoice (an invoice can carry several per
  // line brokers). The table shows each invoice once, with its total
  // brokerage and the brokers on it; paying still happens per broker (the
  // sale popup's Brokers tab, or Pay Selected). With a broker picked in the
  // header, a row holds only that broker's share.
  const invoiceRows = useMemo<InvoiceRow[]>(() => {
    const map = new Map<string, InvoiceRow>();
    for (const d of deals) {
      const key = `${d._source}_${d.id}`;
      let row = map.get(key);
      if (!row) {
        row = {
          key, source: d._source, id: d.id, invoiceNo: d.invoiceNo, date: dealDate(d), dueDate: dealDueDate(d),
          partyId: dealPartyId(d), amount: d.totalAmountUSD ?? 0, deals: [], commission: 0, paid: 0,
        };
        map.set(key, row);
      }
      row.deals.push(d);
      row.commission = Math.round((row.commission + (d.brokerCommissionUSD ?? 0)) * 100) / 100;
      row.paid = Math.round((row.paid + (d.brokerPaidUSD ?? 0)) * 100) / 100;
    }
    // By due date, earliest first (most overdue on top; no due date last);
    // same due date: newest invoice date, then higher number.
    return [...map.values()].sort((a, b) =>
      (a.dueDate ? 0 : 1) - (b.dueDate ? 0 : 1)
      || (a.dueDate ?? '').localeCompare(b.dueDate ?? '')
      || (b.date ?? '').localeCompare(a.date ?? '')
      || (b.invoiceNo ?? '').localeCompare(a.invoiceNo ?? '', undefined, { numeric: true }));
  }, [deals]);

  // Excel-style filters, as on Receivable / Payable.
  const filterColumns = useMemo<FilterColumn<InvoiceRow>[]>(() => [
    { key: 'type',    label: 'Type',       get: r => (r.source === 'sale' ? 'Sale' : 'Buy') },
    { key: 'party',   label: 'Party',      get: r => entityName(r.partyId) },
    { key: 'invoice', label: 'Invoice No', get: r => shortInvoiceNo(r.invoiceNo) },
    { key: 'brokers', label: 'Brokers',    get: r => r.deals.map(d => entityName(d.brokerEntityId)) },
    { key: 'due',     label: 'Due Date',   get: r => rowDueBucket(r), sort: (a, b) => ROW_DUE_ORDER.indexOf(a) - ROW_DUE_ORDER.indexOf(b) },
    { key: 'status',  label: 'Status',     get: r => rowStatusLabel(r) },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [entities]);
  const { rows: matchedRows, filters: colFilters } = useColumnFilters(invoiceRows, filterColumns, { status: DEFAULT_STATUS });
  // With brokers ticked in the Brokers filter, each row shows only their
  // share of the invoice (what the old broker picker in the header did).
  const pickedBrokers = colFilters.selected.brokers;
  const visibleRows = useMemo<InvoiceRow[]>(() => {
    if (!pickedBrokers) return matchedRows;
    return matchedRows.map(r => {
      const deals = r.deals.filter(d => pickedBrokers.includes(entityName(d.brokerEntityId)));
      const sum = (f: (d: Deal) => number) => Math.round(deals.reduce((t, d) => t + f(d), 0) * 100) / 100;
      return { ...r, deals, commission: sum(d => d.brokerCommissionUSD ?? 0), paid: sum(d => d.brokerPaidUSD ?? 0) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedRows, pickedBrokers, entities]);
  const pickedBrokerId = pickedBrokers?.length === 1 ? brokers.find(b => b.companyName === pickedBrokers[0])?.id : undefined;
  const paging = usePaging(visibleRows, `${dateFilter}|${customFrom}|${customTo}|${colFilters.stamp}`);
  const rowTotals = useMemo(() => visibleRows.reduce((t, r) => ({
    commission: t.commission + r.commission, paid: t.paid + r.paid, amount: t.amount + r.amount,
  }), { commission: 0, paid: 0, amount: 0 }), [visibleRows]);
  const wrapRef = useRef<HTMLDivElement>(null);
  useFitHeight(wrapRef, 56, 280, [visibleRows.length > 0, loading]);

  // Export: CSV of every filtered row (not just this page), or Print List
  // (all rows on screen for the print, then back to pages).
  const [printAll, setPrintAll] = useState(false);
  useEffect(() => {
    if (!printAll) return;
    let cancelled = false;
    const after = () => setPrintAll(false);
    globalThis.addEventListener('afterprint', after);
    requestAnimationFrame(() => { if (!cancelled) requestAnimationFrame(() => { if (!cancelled) globalThis.print(); }); });
    return () => { cancelled = true; globalThis.removeEventListener('afterprint', after); };
  }, [printAll]);
  function handleExportCSV() {
    downloadCSV('brokerage',
      ['Due Date', 'Date', 'Type', 'Brokers', 'Invoice No', 'Party', 'Amount', 'Brokerage', 'Rate', 'Paid', 'Outstanding', 'Status'],
      visibleRows.map(r => {
        const rates = [...new Set(r.deals.map(d => d.brokerCommissionPercent).filter((x): x is number => x != null))];
        return [
          r.dueDate ?? '', r.date ?? '', r.source === 'sale' ? 'Sale' : 'Buy',
          r.deals.map(d => entityName(d.brokerEntityId)).join(', '), r.invoiceNo, entityName(r.partyId),
          r.amount.toFixed(2), r.commission.toFixed(2), rates.length ? `${rates.join('% + ')}%` : '',
          r.paid.toFixed(2), Math.max(0, r.commission - r.paid).toFixed(2), rowStatusLabel(r),
        ];
      }));
  }

  /** Open an invoice's brokerage: a sale opens the sale popup on its Brokers tab. */
  function openRow(r: InvoiceRow) {
    const pick = r.deals.find(d => commissionOutstanding(d) > 0.005) ?? r.deals[0];
    setDetailInv(pick);
  }

  // Only deals with something left to pay are selectable for a bulk payment —
  // an already-settled deal has nothing to include. Covers both sale and
  // purchase-side commissions, AND both header and per-line deals now —
  // BulkRecordBrokerPaymentModal branches on each row's own `_source` (which
  // collection) and whether it's a LineDeal (whether to tag the payment with
  // a `brokerEntityId` and skip the invoice's own header-level broker
  // fields). Selection is keyed by `dealKey`, not the bare invoice id —
  // several LineDeal rows can share the same invoice id (one per broker on
  // that invoice), so the id alone would collide between them.
  // Ticking an invoice row picks every broker on it with something owed.
  const payableRows = useMemo(() => visibleRows.filter(r => r.commission - r.paid > 0.005), [visibleRows]);
  const allPayableSelected = payableRows.length > 0 && payableRows.every(r => selectedIds.has(r.key));
  function toggleSelectAll() {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allPayableSelected) payableRows.forEach(r => next.delete(r.key));
      else payableRows.forEach(r => next.add(r.key));
      return next;
    });
  }
  function toggleSelect(key: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }
  const selectedDeals = useMemo(
    () => visibleRows.filter(r => selectedIds.has(r.key)).flatMap(r => r.deals.filter(d => commissionOutstanding(d) > 0.005)),
    [visibleRows, selectedIds],
  );
  const selectionOutstanding = useMemo(
    () => selectedDeals.reduce((s, inv) => s + commissionOutstanding(inv), 0),
    [selectedDeals],
  );

  return (
    <AppLayout title="Brokers" subtitle="Commission owed and paid to your brokers" headerActions={<>
        {/* All on the right: Custom dates (only with Custom), the period tabs, then Clear. */}
        {dateFilter === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="date" className="tb-date" value={customFrom} max={customTo || todayISO()}
              onChange={e => setCustomFrom(e.target.value)} aria-label="From date" />
            <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600 }}>→</span>
            <input type="date" className="tb-date" value={customTo} min={customFrom || undefined} max={todayISO()}
              onChange={e => setCustomTo(e.target.value)} aria-label="To date" />
          </div>
        )}
        <div className="tb-seg" role="tablist" aria-label="Period">
          {([['1m', '1M'], ['3m', '3M'], ['6m', '6M'], ['1y', '1Y'], ['2y', '2Y'], ['custom', 'Custom']] as [DateFilter, string][]).map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={dateFilter === k}
              className={dateFilter === k ? 'on' : undefined} onClick={() => setDateFilter(k)}
              title={{ '1m': 'Overdue + due in the next month', '3m': 'Overdue + due in the next 3 months', '6m': 'Overdue + due in the next 6 months', '1y': 'Overdue + due in the next year', '2y': 'Overdue + due in the next 2 years', custom: 'Due between two dates' }[k]}>
              {label}
            </button>
          ))}
        </div>
        {/* Always takes its room (hidden when there's nothing to clear), so
            the tabs never shift when it appears. */}
        <button type="button" className="tb-clear"
          style={dateFilter !== '3m' || !onDefaultFilters(colFilters.selected) ? undefined : { visibility: 'hidden' }}
          aria-hidden={!(dateFilter !== '3m' || !onDefaultFilters(colFilters.selected))}
          tabIndex={dateFilter !== '3m' || !onDefaultFilters(colFilters.selected) ? 0 : -1}
          onClick={() => { setDateFilter('3m'); colFilters.clear(); colFilters.set('status', DEFAULT_STATUS); }}>Clear</button>
        <div className="tb-group">
          <Dropdown align="right" trigger={() => (
            <button type="button" className="tb-group-btn" title="Export" aria-label="Export"><IconExportSm /></button>
          )}>
            <button type="button" className="dropdown-item" onClick={handleExportCSV}>Export CSV</button>
            <button type="button" className="dropdown-item" onClick={() => setPrintAll(true)}>Print List</button>
          </Dropdown>
        </div>
      </>}>


      {/* ── Selection strip — same small compact-chip style as Purchase's own
           selection row: always rendered with real content (totals for the
           whole filtered list when nothing's selected, swapping to totals
           for just the selection), not a primary-tinted bar that's only
           ever shown/hidden. ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {(selectedIds.size > 0 ? [
          { label: 'Selected', val: `${selectedIds.size} invoice${selectedIds.size !== 1 ? 's' : ''}` },
          { label: 'Outstanding', val: money(selectionOutstanding) },
        ] : [
          { label: 'Brokerage', val: money(rowTotals.commission) },
          { label: 'Paid', val: money(rowTotals.paid), color: 'var(--success)' },
          { label: 'Outstanding', val: money(Math.max(0, rowTotals.commission - rowTotals.paid)), color: rowTotals.commission - rowTotals.paid > 0.005 ? 'var(--danger)' : undefined },
          { label: 'Invoices', val: String(visibleRows.length) },
          { label: 'Brokers', val: String(new Set(visibleRows.flatMap(r => r.deals.map(d => d.brokerEntityId))).size) },
        ] as { label: string; val: string; color?: string }[]).map(({ label, val, color }) => (
          <div key={label} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-sm)', padding: '6px 12px',
            boxShadow: 'var(--shadow-xs)', fontSize: 12,
          }}>
            <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>{label}</span>
            <span style={{ width: 1, height: 12, background: 'var(--border)', flexShrink: 0 }} />
            <span style={{ color: color ?? 'var(--text-1)', fontWeight: 700, fontFamily: 'var(--font-num)' }}>{val}</span>
          </div>
        ))}
        <ColumnFilterChips filters={colFilters} inline />
        {selectedIds.size > 0 && (
          <button onClick={() => setSelectedIds(new Set())} style={{
            display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 700,
            color: 'var(--danger)', background: 'var(--danger-bg)',
            border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
            borderRadius: 'var(--r-sm)', padding: '6px 12px', cursor: 'pointer',
          }}>
            Clear selection
          </button>
        )}
        {selectedIds.size > 0 && (
          <Button variant="primary" size="sm" icon={<IconPay />} onClick={() => setBulkOpen(true)} style={{ marginLeft: 'auto' }}>
            Pay Selected
          </Button>
        )}
      </div>

      {/* ── Table / states ── */}
      {dateFilter === 'custom' && !dateRange ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ marginBottom: 12, color: 'var(--text-3)' }}><IconCalendar /></div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>Select a date range</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Choose a From and To date above to load deals.</div>
        </div>
      ) : loading ? (
        <div className="table-wrap">
          <table>
            <thead><tr>{[16, 90, 80, 140, 90, 90, 70, 90, 90, 90, 75].map((w, i) => (
              <th key={i}><div className="skeleton" style={{ height: 11, width: w, borderRadius: 4 }} /></th>
            ))}</tr></thead>
            <tbody>{[...Array(5)].map((_, i) => (
              <tr key={i} style={{ opacity: 1 - i * 0.18 }}>
                {[16, 70, 60, 110, 80, 80, 50, 80, 80, 80, 60].map((w, j) => (
                  <td key={j}><div className="skeleton" style={{ height: 13, width: w, borderRadius: 4 }} /></td>
                ))}
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : invoiceRows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: 'var(--text-3)' }}><IconEmptyDiamond /></div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No broker deals in this period</div>
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Sales or purchases with a broker assigned will show up here.</div>
        </div>
      ) : visibleRows.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 24px', color: 'var(--text-3)', fontSize: 13 }}>
          Nothing matches these filters.
        </div>
      ) : (
        <>
        <div ref={wrapRef} className="table-wrap table-fit table-fit-stats">
          <table>
            <thead>
              <tr>
                <th style={{ width: 32, textAlign: 'center' }}>
                  <input type="checkbox" checked={allPayableSelected} onChange={toggleSelectAll} title="Select every invoice with brokerage owed" disabled={payableRows.length === 0} />
                </th>
                <th className="th-filterable" style={{ minWidth: 120, whiteSpace: 'nowrap' }}>Due Date<ColumnFilterButton filters={colFilters} colKey="due" label="Due Date" /></th>
                <th>Date</th>
                <th className="th-filterable">Type<ColumnFilterButton filters={colFilters} colKey="type" label="Type" /></th>
                <th className="th-filterable">Brokers<ColumnFilterButton filters={colFilters} colKey="brokers" label="Brokers" /></th>
                <th className="th-filterable">Invoice No<ColumnFilterButton filters={colFilters} colKey="invoice" label="Invoice No" /></th>
                <th className="th-filterable">Party<ColumnFilterButton filters={colFilters} colKey="party" label="Party" /></th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ textAlign: 'right' }} title="Total commission to every broker on this invoice">Brokerage</th>
                <th style={{ textAlign: 'right' }}>Paid</th>
                <th style={{ textAlign: 'right' }}>Outstanding</th>
                <th className="th-filterable">Status<ColumnFilterButton filters={colFilters} colKey="status" label="Status" align="right" /></th>
                <th className="col-actions" />
              </tr>
            </thead>
            <tbody>
              {(printAll ? visibleRows : paging.pageRows).map(r => {
                const owed = Math.max(0, Math.round((r.commission - r.paid) * 100) / 100);
                const payable = owed > 0.005;
                const brokerNames = r.deals.map(d => entityName(d.brokerEntityId));
                // The rate(s) set on the invoice, not brokerage ÷ amount: commission is
                // worked out on the full price (before Keep %), so a division would
                // show odd figures like 1.02%. Two brokers at different rates: "1% + 0.5%".
                const rates = [...new Set(r.deals.map(d => d.brokerCommissionPercent).filter((x): x is number => x != null))];
                const pct = rates.length ? rates.join('% + ') : null;
                return (
                  <tr key={r.key} onClick={() => openRow(r)} style={{ cursor: 'pointer' }}>
                    <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.has(r.key)} disabled={!payable}
                        onChange={() => toggleSelect(r.key)} title={payable ? undefined : 'Nothing left to pay'} />
                    </td>
                    <td style={{ minWidth: 120, whiteSpace: 'nowrap' }}>
                      {r.dueDate ? (() => {
                        const dd = dueDateDisplay(r.dueDate);
                        const overdue = payable && dd.isOverdue;
                        const soon    = payable && dd.isSoon;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <span style={{ fontFamily: 'var(--font-num)', fontWeight: 500, color: overdue ? 'var(--danger)' : soon ? '#c2410c' : 'var(--text-2)' }}>{dd.label}</span>
                            {payable && <span style={{ fontSize: 10, fontWeight: 600, lineHeight: 1, color: overdue ? 'var(--danger)' : soon ? '#f59e0b' : 'var(--text-3)' }}>{dd.badge}</span>}
                          </div>
                        );
                      })() : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td style={{ fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>
                      {r.date ? new Date(r.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td>
                      <span style={{
                        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '1px 6px', borderRadius: 4,
                        background: r.source === 'purchase' ? 'var(--warning-bg)' : 'var(--primary-light)',
                        color: r.source === 'purchase' ? '#c2410c' : 'var(--primary)',
                      }}>{r.source === 'purchase' ? 'Buy' : 'Sale'}</span>
                    </td>
                    <td style={{ maxWidth: 240 }}>
                      {/* One broker per line (an invoice can have two or more). */}
                      <div title={brokerNames.join(', ')} style={{ lineHeight: 1.35, color: 'var(--text-1)', fontWeight: 600 }}>
                        {r.deals.map((d, k) => (
                          <div key={`${d.brokerEntityId}_${k}`} className="cell-clip" style={{ maxWidth: 240 }}>{brokerNames[k]}</div>
                        ))}
                      </div>
                    </td>
                    <td><span className="sm-ref" title={r.invoiceNo}>{shortInvoiceNo(r.invoiceNo)}</span></td>
                    <td style={{ maxWidth: 220 }}><span className="cell-clip" title={entityName(r.partyId)}>{entityName(r.partyId)}</span></td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)' }}>{money(r.amount)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 700, color: 'var(--text-1)' }}>{money(r.commission)}</span>
                      {pct != null && <span style={{ color: 'var(--text-3)', marginLeft: 4 }}>({pct}%)</span>}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--success)', fontWeight: 600 }}>{r.paid > 0 ? money(r.paid) : <span style={{ opacity: 0.4 }}>—</span>}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, color: payable ? 'var(--danger)' : 'var(--text-3)' }}>{money(owed)}</td>
                    <td><PayBadge inv={{ brokerPaymentStatus: rowStatus(r), brokerPaidUSD: r.paid, brokerCommissionUSD: r.commission }} /></td>
                    <td className="col-actions" onClick={e => e.stopPropagation()}>
                      {payable && (
                        <button className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '5px 10px' }}
                          onClick={() => {
                            // One broker owed on a purchase: pay it straight away; otherwise open the brokerage.
                            const owing = r.deals.filter(d => commissionOutstanding(d) > 0.005);
                            if (r.source === 'purchase' && owing.length === 1) setPayTarget(owing[0]); else openRow(r);
                          }}>
                          <IconPay /> Pay
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={7} style={{ fontWeight: 700 }}>Total · {visibleRows.length} invoice{visibleRows.length !== 1 ? 's' : ''}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(rowTotals.amount)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(rowTotals.commission)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, color: 'var(--success)' }}>{money(rowTotals.paid)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-num)', fontWeight: 700, color: rowTotals.commission - rowTotals.paid > 0.005 ? 'var(--danger)' : 'var(--text-3)' }}>{money(Math.max(0, rowTotals.commission - rowTotals.paid))}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          </table>
        </div>
        <Paging page={paging.page} totalPages={paging.totalPages} total={paging.total} onChange={paging.setPage} noun="invoice" />
        </>
      )}

      <RecordBrokerPaymentModal
        invoice={payTarget}
        source={payTarget?._source ?? 'sale'}
        onClose={() => setPayTarget(null)}
        brokerName={payTarget ? entityName(payTarget.brokerEntityId) : undefined}
        lineBrokerEntityId={payTarget && isLineDeal(payTarget) ? payTarget.brokerEntityId : undefined}
      />

      {bulkOpen && (
        <BulkRecordBrokerPaymentModal
          invoices={selectedDeals.filter(inv => commissionOutstanding(inv) > 0.005)}
          entities={entities}
          onClose={paidCount => {
            setBulkOpen(false);
            if (paidCount) setSelectedIds(new Set());
          }}
        />
      )}

      {/* A sale opens the same sale popup as everywhere else, on its
          Brokers tab (commission, payouts, Pay); purchases keep their own
          commission popup for now. */}
      <SalePreviewModal
        invoice={detailInv?._source === 'sale' ? (sales.find(x => x.id === detailInv.id) ?? null) : null}
        entityName={id => entityName(id)}
        onClose={() => setDetailInv(null)}
        onPay={inv => setSalePayTarget(inv)}
        initialTab="brokers"
        highlightBrokerId={pickedBrokerId}
      />
      <RecordSalePaymentModal
        invoice={salePayTarget}
        onClose={() => setSalePayTarget(null)}
        entityName={salePayTarget ? entityName(salePayTarget.toEntityId) : undefined}
      />

      <CommissionDetailModal
        invoice={detailInv?._source === 'purchase' ? detailInv : null}
        onClose={() => setDetailInv(null)}
        onPay={inv => { setDetailInv(null); setPayTarget(inv); }}
        entities={entities}
        allDeals={deals}
      />
    </AppLayout>
  );
}
